import { useState, useCallback, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, RefreshCw, XCircle, CloudUpload } from "lucide-react";
import { toast } from "sonner";
import { useAction, useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import { useTranslation } from "react-i18next";
import { useWorkspaceQuery, useWorkspaceId } from "@/hooks";
import { StateCard } from "@/components/ui/StateCard";
import { Button } from "@/components/ui/primitives";
import type { Id } from "@/packages/types";

// ─── Props ──────────────────────────────────────────────────────────────────

interface StepExtractingProps {
  auditId: Id<"audits">;
  onExtractionComplete: (standardId: Id<"officialStandards">) => void;
  onError: (error: string) => void;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const EXTRACTION_TIMEOUT_MS = 180_000;
const ACCEPTED_EXTENSIONS = [".pdf", ".docx"];

// ─── Internal file state ────────────────────────────────────────────────────

type ExtractionPhase = "idle" | "uploading" | "extracting" | "done" | "error";

// ─── SingleFileDropZone (local) ─────────────────────────────────────────────

function SingleFileDropZone({
  accept,
  onFileSelected,
  disabled,
}: {
  accept: string;
  onFileSelected: (file: File) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation("audits");
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback(
    (file: File): boolean => {
      const name = file.name.toLowerCase();
      const isValid = ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
      if (!isValid) {
        toast.error(t("wizard.extraction.invalidFileType"));
      }
      return isValid;
    },
    [t],
  );

  const handleBrowse = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      if (fileList && fileList.length > 0) {
        const file = fileList[0];
        if (validateFile(file)) {
          onFileSelected(file);
        }
      }
      e.target.value = "";
    },
    [onFileSelected, validateFile],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) setIsDragOver(true);
    },
    [disabled],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (disabled) return;

      const droppedFiles = Array.from(e.dataTransfer.files);
      if (droppedFiles.length > 0) {
        const file = droppedFiles[0];
        if (validateFile(file)) {
          onFileSelected(file);
        }
      }
    },
    [disabled, onFileSelected, validateFile],
  );

  return (
    <button
      type="button"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`group flex w-full flex-col items-center justify-center rounded-lg border border-dashed px-4 py-6 text-center transition-all duration-200 cursor-pointer ${
        isDragOver
          ? "border-accent bg-accent/5 ring-2 ring-accent/30"
          : "border-border hover:border-accent/50"
      }`}
      onClick={handleBrowse}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="hidden"
      />
      <CloudUpload
        size={28}
        className={`mb-2 transition-transform duration-200 ${
          isDragOver
            ? "text-accent scale-110"
            : "text-muted group-hover:scale-110"
        }`}
      />
      <span className="text-[13px] font-medium text-primary mb-0.5">
        {t("wizard.extraction.dropZoneLabel")}
      </span>
      <span className="text-[11px] text-muted">
        {t("wizard.extraction.dropZoneHint")}
      </span>
    </button>
  );
}

// ─── StepExtracting ─────────────────────────────────────────────────────────

export function StepExtracting({
  auditId,
  onExtractionComplete,
  onError,
}: StepExtractingProps) {
  const { t } = useTranslation("audits");
  const progressMessages = [
    t("wizard.extraction.progressUploading"),
    t("wizard.extraction.progressReading"),
    t("wizard.extraction.progressExtracting"),
  ];
  const workspaceId = useWorkspaceId();
  const [phase, setPhase] = useState<ExtractionPhase>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [messageIndex, setMessageIndex] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const readyFiredRef = useRef(false);

  const generateUploadUrl = useMutation(
    api.document_mutations.generateUploadUrl,
  );
  const extractAction = useAction(
    api.user_standard_actions.uploadAndExtractUserStandard,
  );

  // Reactive query: watch for standard status changes
  const { data: standard } = useWorkspaceQuery(
    api.admin_standards_queries.getUserStandardByAuditId,
    { auditId },
  );

  // Rotate progress messages while extracting
  useEffect(() => {
    if (phase !== "uploading" && phase !== "extracting") return;
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % progressMessages.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [phase, progressMessages.length]);

  // Timeout after 120s
  useEffect(() => {
    if (phase !== "uploading" && phase !== "extracting") return;
    const timer = setTimeout(() => {
      setTimedOut(true);
    }, EXTRACTION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  // Auto-advance when extraction is complete
  useEffect(() => {
    if (!standard) return;

    if (standard.status === "extracted" && !readyFiredRef.current) {
      readyFiredRef.current = true;
      setPhase("done");
      onExtractionComplete(standard._id);
    }

    if (standard.status === "extraction_failed" && phase !== "error") {
      setPhase("error");
      const msg = standard.extractionError ?? "Extraction failed";
      setErrorMessage(msg);
      onError(msg);
    }
  }, [standard, onExtractionComplete, onError, phase]);

  // Handle file selection: upload + trigger extraction
  const handleFileSelected = useCallback(
    async (file: File) => {
      setPhase("uploading");
      setErrorMessage(null);
      setTimedOut(false);
      readyFiredRef.current = false;

      try {
        // 1. Get upload URL
        const uploadUrl = await generateUploadUrl({});
        const result = await fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": file.type },
          body: file,
        });
        const { storageId } = (await result.json()) as {
          storageId: Id<"_storage">;
        };

        // 2. Trigger extraction action
        setPhase("extracting");
        await extractAction({
          auditId,
          workspaceId,
          documentStorageId: storageId,
          fileName: file.name,
        });

        // The action completes but we rely on the reactive query
        // to detect when status is "extracted"
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : t("wizard.extraction.uploadFailed");
        setPhase("error");
        setErrorMessage(msg);
        onError(msg);
      }
    },
    [generateUploadUrl, extractAction, auditId, workspaceId, onError, t],
  );

  const handleRetry = useCallback(() => {
    setPhase("idle");
    setErrorMessage(null);
    setTimedOut(false);
    readyFiredRef.current = false;
  }, []);

  // Error state
  if (phase === "error") {
    return (
      <div className="flex flex-col items-center gap-6 py-8">
        <StateCard variant="empty">
          <StateCard.Icon />
          <StateCard.Title>{t("wizard.extraction.failed")}</StateCard.Title>
          <StateCard.Description>
            {t("wizard.extraction.failedDescription")}
          </StateCard.Description>
        </StateCard>
        <div className="flex items-center gap-2 text-red-500">
          <XCircle className="h-4 w-4" />
          <span className="text-[12px] font-medium">
            {errorMessage ?? t("wizard.extraction.failed")}
          </span>
        </div>
        <Button variant="primary" icon={RefreshCw} onClick={handleRetry}>
          {t("wizard.extraction.tryAgain")}
        </Button>
      </div>
    );
  }

  // Timeout state
  if (timedOut && phase !== "done") {
    return (
      <div className="flex flex-col items-center gap-6 py-8">
        <StateCard variant="empty">
          <StateCard.Icon />
          <StateCard.Title>
            {t("wizard.extraction.timedOutTitle")}
          </StateCard.Title>
          <StateCard.Description>
            {t("wizard.extraction.timedOutDescription")}
          </StateCard.Description>
        </StateCard>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              setTimedOut(false);
            }}
          >
            {t("wizard.extraction.keepWaiting")}
          </Button>
          <Button variant="primary" icon={RefreshCw} onClick={handleRetry}>
            {t("wizard.extraction.tryAgain")}
          </Button>
        </div>
      </div>
    );
  }

  // Idle state: show file dropzone
  if (phase === "idle") {
    return (
      <div className="flex flex-col gap-6 py-4">
        <div className="text-center">
          <h3 className="text-sm font-medium text-primary mb-1">
            {t("wizard.extraction.uploadTitle")}
          </h3>
          <p className="text-[12px] text-muted">
            {t("wizard.extraction.uploadDescription")}
          </p>
        </div>
        <SingleFileDropZone
          accept=".pdf,.docx"
          onFileSelected={handleFileSelected}
          disabled={false}
        />
      </div>
    );
  }

  // Uploading / Extracting progress state
  return (
    <div className="flex flex-col items-center gap-6 py-8">
      <StateCard variant="ai">
        <StateCard.Icon />
        <StateCard.Title>
          {t("wizard.extraction.extractingTitle")}
        </StateCard.Title>
        <StateCard.Description>
          {t("wizard.extraction.extractingDescription")}
        </StateCard.Description>
      </StateCard>

      <div className="w-full space-y-1">
        <AnimatePresence mode="wait">
          <motion.span
            key={messageIndex}
            className="block text-center text-[11px] font-medium text-accent"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
          >
            {progressMessages[messageIndex]}
          </motion.span>
        </AnimatePresence>

        <div className="mx-auto mt-1 h-[2px] w-48 rounded-full bg-border/50">
          <motion.div
            className="h-full rounded-full bg-accent"
            initial={{ width: "0%" }}
            animate={{ width: "90%" }}
            transition={{ duration: 15, ease: "easeOut" }}
          />
        </div>
      </div>

      <div className="flex items-center gap-2 text-muted">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-[11px]">
          {phase === "uploading"
            ? t("wizard.extraction.uploading")
            : t("wizard.extraction.processing")}
        </span>
      </div>
    </div>
  );
}
